import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { QUICK_REACTIONS } from '../../lib/reactions';
import { rememberReaction } from '../../lib/emojiSet';
import { lightTap } from '../../lib/haptics';
import { EmojiSheet } from './EmojiSheet';

const LONG_PRESS_MS = 450;
const MOVE_TOLERANCE_PX = 10;
/** Keeps the bar clear of the screen's top edge (status bar and header). */
const TOP_CLEARANCE_PX = 72;
const BAR_HEIGHT_PX = 52;

interface ReactionMenuProps {
  /** The message. Called again for the copy shown above the dimmed screen. */
  children: (api: { open: () => void }) => ReactNode;
  align: 'left' | 'right';
  /** The signed-in user's current reaction on this message. */
  mine?: string;
  onReact: (emoji: string) => void;
  /** The message text; "Copy" is offered when given. */
  copyText?: string;
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    // Fall back for web views that refuse the clipboard API.
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try { document.execCommand('copy'); } catch { /* nothing more to try */ }
  area.remove();
}

/**
 * React to a message like a messenger does: long-press on a phone, or the small
 * button on hover (and right-click) with a mouse. Opens the quick reactions and a
 * "+" for more, with "Copy", over a dimmed screen that keeps the message in view.
 */
export function ReactionMenu({ children, align, mine, onReact, copyText }: ReactionMenuProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const press = useRef<{ x: number; y: number; timer: number } | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [sheet, setSheet] = useState(false);

  const open = useCallback(() => {
    const el = rowRef.current;
    if (el) setRect(el.getBoundingClientRect());
  }, []);
  const close = useCallback(() => setRect(null), []);

  const cancelPress = useCallback(() => {
    if (press.current) window.clearTimeout(press.current.timer);
    press.current = null;
  }, []);
  useEffect(() => cancelPress, [cancelPress]);

  useEffect(() => {
    if (!rect) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rect, close]);

  function onPointerDown(e: PointerEvent) {
    if (e.pointerType === 'mouse') return;
    cancelPress();
    press.current = { x: e.clientX, y: e.clientY, timer: window.setTimeout(() => { press.current = null; lightTap(); open(); }, LONG_PRESS_MS) };
  }
  function onPointerMove(e: PointerEvent) {
    const p = press.current;
    if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > MOVE_TOLERANCE_PX) cancelPress();
  }

  function choose(emoji: string) {
    rememberReaction(emoji);
    lightTap();
    onReact(emoji);
    close();
    setSheet(false);
  }

  const coarse = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches);

  // The bar goes above the message, or below it when there is no room above.
  const placeBelow = rect ? rect.top < TOP_CLEARANCE_PX + BAR_HEIGHT_PX : false;
  const barTop = rect ? (placeBelow ? rect.bottom + 8 : rect.top - BAR_HEIGHT_PX - 6) : 0;
  const menuTop = rect ? (placeBelow ? barTop + BAR_HEIGHT_PX + 6 : rect.bottom + 8) : 0;

  return (
    <>
      <div
        ref={rowRef}
        className="w-full"
        style={{
          visibility: rect ? 'hidden' : undefined,
          ...(coarse ? { WebkitTouchCallout: 'none', userSelect: 'none', WebkitUserSelect: 'none' } : null),
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={cancelPress}
        onPointerCancel={cancelPress}
        onPointerLeave={cancelPress}
        onContextMenu={(e) => { e.preventDefault(); cancelPress(); open(); }}
      >
        {children({ open })}
      </div>

      {rect && createPortal(
        <div className="fixed inset-0 z-[60]" role="dialog" aria-label="React to this message">
          <div className="absolute inset-0 bg-black/70" onClick={close} />
          {/* The message again, above the dimmed screen, in the same place. */}
          <div className="absolute pointer-events-none" style={{ left: rect.left, top: rect.top, width: rect.width }}>
            {children({ open: () => {} })}
          </div>
          <div
            className="absolute flex items-center gap-0.5 rounded-full bg-app-card border border-app-border shadow-xl px-2 py-1.5"
            style={{ top: barTop, [align === 'right' ? 'right' : 'left']: 12 }}
          >
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => choose(emoji)}
                aria-label={`React with ${emoji}`}
                aria-pressed={mine === emoji}
                className={`sk-hit rounded-full flex items-center justify-center text-[length:calc(24px*var(--text-scale))] active:scale-90 transition-transform ${mine === emoji ? 'bg-gold/25 ring-1 ring-gold' : ''}`}
                style={{ width: 'min(38px, calc((100vw - 76px) / 8))', height: 38 }}
              >
                {emoji}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setSheet(true); close(); }}
              aria-label="More reactions"
              className="rounded-full flex items-center justify-center bg-app-border text-app-fg-2 text-xl active:scale-90 transition-transform sk-hit"
              style={{ width: 'min(38px, calc((100vw - 76px) / 8))', height: 38 }}
            >
              +
            </button>
          </div>
          {copyText ? (
            <div
              className="absolute rounded-2xl bg-app-card border border-app-border shadow-xl p-1 w-44"
              style={{ top: menuTop, [align === 'right' ? 'right' : 'left']: 12 }}
            >
              <button
                type="button"
                onClick={() => { void copyToClipboard(copyText); close(); }}
                className="w-full flex items-center justify-between rounded-xl px-3.5 py-2.5 text-subhead text-app-fg active:bg-app-border sk-hit"
              >
                <span>Copy</span>
                <i className="ti ti-copy text-app-muted" />
              </button>
            </div>
          ) : null}
        </div>,
        document.body,
      )}

      {sheet && <EmojiSheet mine={mine} onPick={choose} onClose={() => setSheet(false)} />}
    </>
  );
}
